<template>
  <div>
    <app-spinner v-if="!is_loaded" />
    <div v-else class="employee">
      <app-cells>
        <h2 class="title">Issued items</h2>
        <app-button @click.native="onOpenModalForm('issue')" size="small">Issue item</app-button>
      </app-cells>
      <v-client-table
        :data="table_issue.items"
        :columns="table_issue.columns"
        :options="table_issue.options"
        class="table-default"
      >
        <template #issue_date="{ row }">{{ dateFormat(row.issue_date) }}</template>
        <template #expire_date="{ row }">{{ dateFormat(row.expire_date) }}</template>
        <template #price="{ row }">{{ row.price.toLocaleString() }}</template>
        <template #scancopy="{ row }">
          <div class="table-default__controls">
            <button
              v-if="row.scancopy"
              @click="onDownloadScanCopy(row.id, row.scancopy, 'issue')"
              type="button"
            >
              <img src="@/assets/img/download-table-blue-icon.svg" alt="">
            </button>
            <button v-else type="button" disabled><img src="@/assets/img/not-check-red-icon.svg" alt=""></button>
          </div>
        </template>
        <template v-if="is_general_manager" #controls="{ row }">
          <div class="table-default__controls">
            <button @click="onShowModalDelete(row, 'issue')" type="button">
              <img src="@/assets/img/delete-table-icon.svg" alt="">
            </button>
          </div>
        </template>
      </v-client-table>
      <app-cells>
        <h2 class="title">Medical examination</h2>
        <app-button @click.native="onOpenModalForm('medical')" size="small">Add examination</app-button>
      </app-cells>
      <v-client-table
        :data="table_medical.items"
        :columns="table_medical.columns"
        :options="table_medical.options"
        class="table-default"
      >
        <template #date="{ row }">{{ dateFormat(row.date) }}</template>
        <template #valid_until="{ row }">{{ dateFormat(row.valid_until) }}</template>
        <template #result="{ row }">{{ row.result.name }}</template>
        <template #report="{ row }">
          <div class="table-default__controls">
            <div
              v-tooltip="{ content: row.report, placement: 'left', offset: 10 }"
              class="table-default__activity table-default__activity--notes"
              :class="{'table-default__activity--active': row.report}"
            ></div>
          </div>
        </template>
        <template #controls="{ row }">
          <div class="table-default__controls">
            <button @click="onShowModalEdit(row, 'medical')" type="button">
              <img src="@/assets/img/edit-table-icon.svg" alt="">
            </button>
            <button @click="onShowModalDelete(row, 'medical')" type="button">
              <img src="@/assets/img/delete-table-icon.svg" alt="">
            </button>
          </div>
        </template>
      </v-client-table>
    </div>
    <modal
      name="issue"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('issue')" class="popup__close" type="button"></button>
        <div class="popup__title">{{ current_modal_state === 'create' ? 'Issue item' : 'Edit item' }}</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckModalForm">
            <app-form-group label="Issue date" required>
              <app-date-picker
                v-model="issue.issue_date"
                placeholder="Specify a date"
                :error="$v.issue.issue_date.$error"
              />
              <template #error>
                <div v-if="$v.issue.issue_date.$dirty && !$v.issue.issue_date.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Expected time to use (month)" label-for="expire_date" required>
              <app-input
                @change.native="$v.issue.expire_date.$touch()"
                v-model="issue.expire_date"
                type="number"
                id="expire_date"
                placeholder="Expected time to use (month)"
                name="expire_date"
                :error="$v.issue.expire_date.$error"
              />
              <template #error>
                <div v-if="$v.issue.expire_date.$dirty && !$v.issue.expire_date.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Item" required>
              <app-select
                v-model="issue.item"
                :reduce="item => item.id"
                :options="issuedequipmentitem_options"
                placeholder="Item"
                :error="$v.issue.item.$error"
              />
              <template #error>
                <div v-if="$v.issue.item.$dirty && !$v.issue.item.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Price ($)" label-for="issue_price" required>
              <app-input
                @change.native="$v.issue.price.$touch()"
                v-model="issue.price"
                type="number"
                id="issue_price"
                placeholder="Price ($)"
                name="issue_price"
                :error="$v.issue.price.$error"
              />
              <template #error>
                <div v-if="$v.issue.price.$dirty && !$v.issue.price.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Scan copy">
              <app-uploader
                v-model="issue.scancopy"
                :extensions="[
                  'application/msword',
                  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                  'application/vnd.ms-excel',
                  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                  'application/zip',
                  'application/pdf',
                  'image/jpeg',
                  'image/png',
                  'image/tiff',
                  'image/bmp'
                ]"
                formDataFormats="DOC,DOCX,XLS,XLSX,ZIP,PDF,JPEG,BMP,PNG,TIFF"
                class="custom-uploader--flex"
              />
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button :disabled="$v.issue.$error" type="submit" ref="submit_issue">
                {{ current_modal_state === 'create' ? 'Issue item' : 'Save' }}
              </app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="medical"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('medical')" class="popup__close" type="button"></button>
        <div class="popup__title">{{ current_modal_state === 'create' ? 'Add' : 'Edit' }} medical examination</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckModalForm">
            <app-form-group label="Date" required>
              <app-date-picker
                v-model="medical.date"
                placeholder="Specify a date"
                :error="$v.medical.date.$error"
              />
              <template #error>
                <div v-if="$v.medical.date.$dirty && !$v.medical.date.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Valid until" required>
              <app-date-picker
                v-model="medical.valid_until"
                placeholder="Specify a date"
                :error="$v.medical.valid_until.$error"
              />
              <template #error>
                <div v-if="$v.medical.valid_until.$dirty && !$v.medical.valid_until.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Result" required>
              <app-select
                v-model="medical.result"
                :options="examination_result_options"
                :clearable="false"
                placeholder="Result"
                class="select"
                :error="$v.medical.result.$error"
              />
              <template #error>
                <div v-if="$v.medical.result.$dirty && !$v.medical.result.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group v-if="medical.result && medical.result.report_required" label="Report" label-for="report" required>
              <app-textarea
                v-model="medical.report"
                placeholder="Write a report"
                id="report"
                rows="5"
                :error="$v.medical.report.$error"
              />
              <template #error>
                <div v-if="$v.medical.report.$dirty && !$v.medical.report.required">Required field</div>
              </template>
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button :disabled="$v.medical.$error" type="submit" ref="submit_medical">
                {{ current_modal_state === 'create' ? 'Add examination' : 'Save' }}
              </app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="delete-issue"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('delete-issue')" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--flex">
          <img src="@/assets/img/warning-round-icon.svg" alt="">
          Delete Record
        </div>
        <div class="popup__body">
          <div class="popup__subt">Are you sure you want to delete this issue item?</div>
          <div v-if="current_modal_data && current_modal_data.item" class="popup__info">
            {{ current_modal_data.item.name }}
          </div>
          <app-cells position="center" :indent="false">
            <app-button @click.native="$modal.hide('delete-issue')" theme="dark">Cancel</app-button>
            <app-button @click.native="onTableControlsDelete" ref="delete_issue">Yes</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
    <modal
      name="delete-medical"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('delete-medical')" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--flex">
          <img src="@/assets/img/warning-round-icon.svg" alt="">
          Delete Record
        </div>
        <div class="popup__body">
          <div class="popup__subt">Are you sure you want to delete this medical examination?</div>
          <div v-if="current_modal_data" class="popup__info">
            {{ dateFormat(current_modal_data.date) }}
          </div>
          <app-cells position="center" :indent="false">
            <app-button @click.native="$modal.hide('delete-medical')" theme="dark">Cancel</app-button>
            <app-button @click.native="onTableControlsDelete" ref="delete_medical">Yes</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { required, requiredIf } from 'vuelidate/lib/validators'
import {
  deleteEmployeesIssue,
  deleteEmployeesMedicalExamination,
  geEmployeesMedicalExaminationResult,
  geIssuedEquipmentItems,
  getEmployeesIssueList,
  getEmployeesMedicalExamination,
  patchEmployeesMedicalExamination,
  postEmployeesIssue,
  postEmployeesMedicalExamination,
} from '@/http'
import { format, parse } from 'date-fns'
import axios from 'axios'

export default {
  name: 'EmployeeOther',
  data() {
    return {
      is_loaded: false,
      medical: {},
      issue: {},
      current_modal_state: 'create',
      current_modal_type: '',
      current_modal_data: {},
      issuedequipmentitem_options: [],
      examination_result_options: [],
      table_medical: {
        items: [],
        columns: ['date', 'valid_until', 'result', 'report', 'controls'],
        options: {
          headings: {
            date: 'Date',
            valid_until: 'Valid until',
            result: 'Result',
            report: 'Report',
            controls: ''
          },
          pagination: { show: false },
          sortable: [],
          perPage: 100,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      table_issue: {
        items: [],
        columns: ['issue_date', 'expire_date', 'item.name', 'price', 'scancopy', 'controls'],
        options: {
          headings: {
            'issue_date': 'Issue date',
            'expire_date': 'Expire date',
            'item.name': 'Item',
            'price': 'Price',
            'scancopy': 'Scan copy',
            'controls': ''
          },
          pagination: { show: false },
          sortable: [],
          perPage: 100,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
    }
  },
  validations: {
    issue: {
      issue_date: { required },
      expire_date: { required },
      item: { required },
      price: { required },
    },
    medical: {
      date: { required },
      valid_until: { required },
      result: { required },
      report: {
        required: requiredIf(function () {
          return this.medical.result && this.medical.result.report_required
        })
      },
    },
  },
  created() {
    Promise.all([
      getEmployeesIssueList(this.$route.params.id),
      getEmployeesMedicalExamination(this.$route.params.id)
    ])
      .then(values => {
        this.table_issue.items = values[0].data
        this.table_medical.items = values[1].data

        this.is_loaded = true
      })
    geIssuedEquipmentItems().then(res => { this.issuedequipmentitem_options = res.data })
    geEmployeesMedicalExaminationResult().then(res => { this.examination_result_options = res.data })
  },
  computed: {
    is_general_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager')
    },
  },
  methods: {
    onCheckModalForm() {
      if (this.current_modal_type === 'medical') {
        this.$v.medical.$touch()
        if (this.$v.medical.$invalid) {
          this.$notify({
            type: 'info',
            title: 'Check that the form fields are filled in correctly',
          })
        } else {
          this.sendModalForm()
        }
      } else if (this.current_modal_type === 'issue') {
        this.$v.issue.$touch()
        if (this.$v.issue.$invalid) {
          this.$notify({
            type: 'info',
            title: 'Check that the form fields are filled in correctly',
          })
        } else {
          this.sendModalForm()
        }
      }
    },
    sendModalForm() {
      if (this.current_modal_type === 'medical') {
        this.$refs.submit_medical.preload = true
        if (this.current_modal_state === 'create') {
          postEmployeesMedicalExamination(this.$route.params.id, this.normalizeMedical())
            .finally(() => { this.$refs.submit_medical.preload = false })
            .then(() => {
              getEmployeesMedicalExamination(this.$route.params.id).then(res => {
                this.table_medical.items = res.data
              })
              this.$notify({
                type: 'success',
                title: `Medical examination ${this.dateFormat(this.medical.date)} added`,
              })
              this.$modal.hide('medical')
            })
        } else {
          patchEmployeesMedicalExamination(this.$route.params.id, this.medical.id, this.normalizeMedical())
            .finally(() => { this.$refs.submit_medical.preload = false })
            .then(() => {
              getEmployeesMedicalExamination(this.$route.params.id).then(res => {
                this.table_medical.items = res.data
              })
              this.$notify({
                type: 'success',
                title: 'Medical examination saved',
              })
              this.$modal.hide('medical')
            })
        }
      } else if (this.current_modal_type === 'issue') {
        this.$refs.submit_issue.preload = true
        if (this.current_modal_state === 'create') {
          postEmployeesIssue(this.$route.params.id, this.normalizeIssue())
            .finally(() => { this.$refs.submit_issue.preload = false })
            .then(() => {
              getEmployeesIssueList(this.$route.params.id).then(res => {
                this.table_issue.items = res.data
              })
              this.$notify({
                type: 'success',
                title: 'Issue item added',
              })
              this.$modal.hide('issue')
            })
        }
      }
    },
    normalizeIssue() {
      const normalizedForm = { ...this.issue }

      if (normalizedForm.scancopy && normalizedForm.scancopy.id) {
        normalizedForm.scancopy = normalizedForm.scancopy.id
      } else {
        normalizedForm.scancopy = null
      }
      return normalizedForm
    },
    normalizeMedical() {
      const normalizedForm = { ...this.medical }

      if (normalizedForm.result && typeof normalizedForm.result !== 'string') {
        normalizedForm.result = normalizedForm.result.code
      }
      return normalizedForm
    },
    onTableControlsDelete() {
      if (this.current_modal_type === 'medical') {
        this.$refs.delete_medical.preload = true
        deleteEmployeesMedicalExamination(this.$route.params.id, this.current_modal_data.id)
          .finally(() => { this.$refs.delete_medical.preload = false })
          .then(() => {
            getEmployeesMedicalExamination(this.$route.params.id).then(res => {
              this.table_medical.items = res.data
            })
            this.$notify({
              type: 'success',
              title: `Medical examination ${this.dateFormat(this.current_modal_data.date)} deleted`,
            })
            this.$modal.hide('delete-medical')
          })
      } else if (this.current_modal_type === 'issue') {
        this.$refs.delete_issue.preload = true
        deleteEmployeesIssue(this.$route.params.id, this.current_modal_data.id)
          .finally(() => { this.$refs.delete_issue.preload = false })
          .then(() => {
            getEmployeesIssueList(this.$route.params.id).then(res => {
              this.table_issue.items = res.data
            })
            this.$notify({
              type: 'success',
              title: 'Issue item deleted',
            })
            this.$modal.hide('delete-issue')
          })
      }
    },
    onShowModalEdit(row, type) {
      this.current_modal_state = 'edit'
      this.current_modal_type = type
      this[type] = { ...row }
      this.$modal.show(type)
    },
    onShowModalDelete(row, type) {
      this.current_modal_data = row
      this.current_modal_type = type
      this.$modal.show(`delete-${type}`)
    },
    onOpenModalForm(type) {
      this.current_modal_state = 'create'
      this.current_modal_type = type
      this.medical = {}
      this.issue = {}
      this.$v[type].$reset()
      this.$modal.show(type)
    },
    dateFormat(date) {
      return date ? format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy') : '-'
    },
    onDownloadScanCopy(id, doc_obj, type) {
      axios.get(doc_obj.file, { responseType: 'blob' })
        .then(response => {
          const url = window.URL.createObjectURL(new Blob([response.data]))
          const link = document.createElement('a')
          const extension = doc_obj.file_name.split('.').pop()
          let part_of_name = ''

          if (type === 'issue') part_of_name = 'Issued item'
          link.href = url
          link.setAttribute('download', `${part_of_name} ${id} - ${this.employee.first_name} ${this.employee.last_name}.${extension}`)
          document.body.appendChild(link)
          link.click()
          URL.revokeObjectURL(url)
        })
    },
  }
}
</script>
